import React from "react";
import { ActionIcon, Box, Button, Menu, Text } from "@mantine/core";
import { IconBan, IconDots, IconX } from "@tabler/icons-react";
import StatusCell, { SalesStatusKey } from "./StatusCell";
import styles from "./sales.module.css";

interface DetailPanelProps {
  /** True below the 1200px frame floor: render as an absolute overlay. */
  overlay: boolean;
  title: string;
  meta: string;
  statusKey: SalesStatusKey;
  onClose: () => void;
  children: React.ReactNode;
  /** Footer receipt actions. */
  onPrint: () => void;
  onEmail: () => void;
  emailDisabled?: boolean;
  emailLoading?: boolean;
  /** Opens the void confirmation; omit to hide the "..." menu. */
  onVoid?: () => void;
}

/**
 * Slide-over detail panel. A flex sibling that narrows the list at/above the
 * 1200px frame width; an absolute overlay below it. `panelIn` .22s entrance is
 * the only animation on either screen. Footer carries the secondary actions:
 * Print receipt / Email receipt, plus a "..." menu holding Void when given
 * (refund lives per-payment in the body, not here).
 */
export default function DetailPanel({
  overlay,
  title,
  meta,
  statusKey,
  onClose,
  children,
  onPrint,
  onEmail,
  emailDisabled,
  emailLoading,
  onVoid,
}: DetailPanelProps) {
  return (
    <Box
      className={`${styles.detailPanel} ${
        overlay ? styles.detailPanelOverlay : ""
      }`}
    >
      <Box className={styles.panelHeader}>
        <Box style={{ minWidth: 0 }}>
          <Text className={styles.panelTitle}>{title}</Text>
          <Text className={styles.panelMeta}>{meta}</Text>
        </Box>
        <Box style={{ flex: 1 }} />
        <StatusCell status={statusKey} />
        <ActionIcon
          variant="transparent"
          color="gray"
          aria-label="Close detail"
          onClick={onClose}
        >
          <IconX size={16} stroke={2.4} color="#555" />
        </ActionIcon>
      </Box>

      <Box className={styles.panelBody}>{children}</Box>

      <Box className={styles.panelFooter}>
        <Button
          variant="default"
          radius={7}
          style={{ flex: 1 }}
          onClick={onPrint}
        >
          Print receipt
        </Button>
        <Button
          variant="default"
          radius={7}
          style={{ flex: 1 }}
          onClick={onEmail}
          disabled={emailDisabled}
          loading={emailLoading}
        >
          Email receipt
        </Button>
        {onVoid && (
          <Menu position="top-end" shadow="md" withinPortal>
            <Menu.Target>
              <ActionIcon
                variant="default"
                radius={7}
                size={36}
                aria-label="More actions"
              >
                <IconDots size={18} />
              </ActionIcon>
            </Menu.Target>
            <Menu.Dropdown>
              <Menu.Item
                className={styles.touchTarget}
                color="red"
                leftSection={<IconBan size={16} />}
                onClick={onVoid}
              >
                Void
              </Menu.Item>
            </Menu.Dropdown>
          </Menu>
        )}
      </Box>
    </Box>
  );
}
